/**
 * ============================================================================
 * useLandedCostPreview — ENG-01 Round 2 (async server-side preview)
 * ============================================================================
 *
 * React Query hook that proxies the `calculate_landed_cost_preview` Postgres
 * RPC. Replaces the in-memory `calcLandedCostV2` (now deprecated).
 *
 * Design highlights:
 *   1. **Raw payload contract** — frontend sends only ids + amounts + hs_code.
 *      The RPC enriches arancel/iva from `arancel_1881` and TRMs from
 *      `trm_historico` server-side. No client-side adapter needed.
 *   2. **Native debounce (300ms)** — protects the RPC from slider bursts in
 *      the proforma simulator. Implemented via a small `useDebouncedValue`
 *      helper (no external deps).
 *   3. **Strict error surfacing** — Supabase RPC exceptions (missing TRM,
 *      access denied, missing trm_aduana) propagate as `error` to the UI so
 *      the user sees exactly what failed.
 *   4. **Stable cache key** — JSON.stringify of the debounced payload, so
 *      identical re-renders are deduplicated by React Query.
 *
 * NOT a behavioral change to UI yet: components still consume the in-memory
 * engine via `calcLandedCostV2` (now @deprecated). UI migration happens in
 * the next round, one consumer at a time.
 * ============================================================================
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// RAW input contract — what the frontend sends to the RPC.
// ─────────────────────────────────────────────────────────────────────────────

export interface LandedCostRawItem {
  id: string;
  documento_id: string;
  /** ISO currency of the source document (USD/COP/CNY/EUR/...). */
  moneda_doc: string;
  cantidad: number;
  peso_bruto_kg: number;
  sub_total: number;
  /** Partida arancelaria — server resolves gravamen/IVA via arancel_1881. */
  hs_code?: string | null;
  /** Legacy TLC flag (promotes beneficio to TLC_PURO if true). */
  aplica_tlc?: boolean;
  /** Per-item IVA override (overrides arancel_1881 lookup when present). */
  iva_override?: number | null;
  /** Régimen aduanero: GENERAL | TLC_PURO | LEY_1715 | ZONA_FRANCA. */
  beneficio_aduanero?: "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA" | null;
  /** ICO (bebidas alcohólicas) — grados alcohólicos del producto. */
  grados_alcoholicos?: number | null;
  /** ICO — litros de líquido por unidad (ej. 0.75 botella vino). */
  litros_por_unidad?: number | null;
  /** ICO — tarifa COP por grado-alcohólico-litro (DIAN). */
  tarifa_ico?: number | null;
}

export interface LandedCostRawGasto {
  /** TRACE-01: UUID del gasto en gastos_embarque. Permite correlacionar allocations en el frontend. Opcional para compat hacia atrás. */
  id?: string | null;
  monto: number;
  moneda: string;
  /** Fase C: 'CBM' es ahora una bolsa nativa en el RPC (ya no se colapsa a 'Valor'). */
  base_prorrateo: "Peso" | "Valor" | "CBM";
  /**
   * TRACE-01: base de prorrateo ORIGINAL declarada por el usuario antes de
   * cualquier mapeo. Mantiene compatibilidad con consumidores existentes
   * aunque `base_prorrateo` ya soporte 'CBM' directamente.
   */
  base_declarada?: "CBM" | "Peso" | "Valor" | null;
  trm_pago?: number | null;
  aplica_costeo_inventario?: boolean;
  /**
   * Fase B — Flag DIAN. Si true, el gasto entra a la base gravable (FOB +
   * gastos internacionales) sobre la que se calcula Arancel + IVA. Si false,
   * sólo se capitaliza al CIF financiero. Si null/undefined, el RPC aplica
   * autodetección por concepto. Requiere soporte server-side en
   * `_calculate_landed_cost_internal` (ver migración pendiente).
   */
  incluir_en_base_gravable?: boolean | null;
}

export interface LandedCostPreviewInput {
  /** TRM DIAN del embarque (USD → COP). Si es null/0, el RPC hace fallback a TRM histórica (modo estimado). */
  trm_aduana: number | null;
  /**
   * TRM efectivas por moneda (overrides del Centro de Monedas + mercado).
   * Permite al RPC convertir ítems/gastos en monedas distintas a USD (EUR,
   * CNY, GBP, etc.) sin depender únicamente de trm_aduana.
   */
  trm_map?: Record<string, number>;
  items: LandedCostRawItem[];
  gastos: LandedCostRawGasto[];
  /** Optional FX-rate cutoff date (defaults to today server-side). */
  fx_date?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// RAW output contract — what `_calculate_landed_cost_internal` returns.
// Mirrors the JSONB shape documented in the migration header.
// ─────────────────────────────────────────────────────────────────────────────

export interface LandedCostPreviewItem {
  id: string;
  alloc_peso_cop: number;
  alloc_valor_cop: number;
  /** Fase C: bolsa CBM separada del prorrateo por Valor. 0 si no hay gastos CBM. */
  alloc_cbm_cop?: number;
  cif_cop: number;
  arancel_cop: number;
  iva_cop: number;
  /** ICO (Impuesto al Consumo) capitalizado al landed cost. 0 si no aplica. */
  ico_cop?: number;
  landed_total_cop: number;
  landed_unit_cop: number;
  /** % arancel efectivo aplicado (post-beneficio: TLC/Ley 1715/ZF → 0). */
  arancel_pct: number;
  /** % IVA efectivo aplicado (post-beneficio + iva_override). */
  iva_pct: number;
  beneficio_aduanero: "GENERAL" | "TLC_PURO" | "LEY_1715" | "ZONA_FRANCA";
  /** % arancel crudo del lookup arancel_1881 (sin aplicar beneficio). Para auditoría. */
  arancel_lookup_pct?: number;
}

/**
 * TRACE-01 — Allocation atómica (gasto × ítem) devuelta por el RPC.
 * Garantiza que SUM(monto_cop) por base_efectiva = pool total exacto al céntimo.
 */
export interface LandedCostAllocation {
  /** UUID del gasto (puede ser null si el frontend no lo envió en el input). */
  gasto_id: string | null;
  item_id: string;
  monto_cop: number;
  /** Lo que el usuario declaró: 'CBM' | 'Peso' | 'Valor'. Para etiqueta UI. */
  base_declarada: "CBM" | "Peso" | "Valor";
  /** Fase C: base que el motor realmente usó. 'CBM' es ahora una bolsa propia. */
  base_efectiva: "Peso" | "Valor" | "CBM";
}

export interface LandedCostPreviewResult {
  engine_version: "v2";
  trm_aduana_usada: number;
  /** 'aduana' = TRM oficial DIM (final). 'mercado' = TRM histórica USD (estimado, sin DIM). */
  trm_source?: "aduana" | "mercado";
  fx_date_usada?: string;
  fob_total_cop: number;
  peso_total_kg: number;
  gastos_peso_cop: number;
  gastos_valor_cop: number;
  /** Fase C: subtotal nativo de la bolsa CBM. 0 si no hay gastos prorrateados por CBM. */
  gastos_cbm_cop?: number;
  shortfall_peso: number;
  shortfall_valor: number;
  items: LandedCostPreviewItem[];
  /** TRACE-01 — desglose por (gasto × ítem). Vacío si el RPC aún no lo provee. */
  allocations?: LandedCostAllocation[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Debounce helper — collapses bursty input into a single trailing-edge value.
// Local impl (no extra deps) tuned for slider/typing UX (300ms default).
// ─────────────────────────────────────────────────────────────────────────────

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main hook
// ─────────────────────────────────────────────────────────────────────────────

export interface UseLandedCostPreviewOptions {
  /** ms to wait before firing the RPC after the last input change. */
  debounceMs?: number;
  /** Disable the query (e.g. while parent data is still loading). */
  enabled?: boolean;
}

export function useLandedCostPreview(
  input: LandedCostPreviewInput | null | undefined,
  options: UseLandedCostPreviewOptions = {},
) {
  const { debounceMs = 300, enabled = true } = options;
  const { orgId } = useAuth();

  // Debounce the entire input object. Using JSON.stringify as a stable
  // dependency surrogate so React Query gets a clean cache key.
  const debouncedInput = useDebouncedValue(input, debounceMs);
  const inputKey = debouncedInput ? JSON.stringify(debouncedInput) : null;

  return useQuery<LandedCostPreviewResult, Error>({
    queryKey: ["landed-cost-preview", orgId, inputKey],
    enabled: enabled && !!orgId && !!debouncedInput && hasUsableInput(debouncedInput),
    // Disable retries — RPC errors are deterministic (missing TRM, no auth, etc.)
    retry: false,
    // Re-fetch on focus is wasteful for a deterministic compute → off.
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!orgId) throw new Error("Organización activa no resuelta.");
      if (!debouncedInput) throw new Error("Payload vacío.");

      const { data, error } = await supabase.rpc(
        "calculate_landed_cost_preview" as never,
        {
          p_org_id: orgId,
          p_input: debouncedInput as unknown as never,
        } as never,
      );

      if (error) {
        // Surface Postgres EXCEPTION messages verbatim — they are user-facing
        // and pre-formatted in Spanish (e.g. "Debe definir la TRM Aduanera...",
        // "Tasa de cambio no encontrada para la(s) moneda(s): CNY, EUR").
        throw new Error(error.message);
      }
      if (!data) throw new Error("El RPC no devolvió datos.");
      return data as unknown as LandedCostPreviewResult;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Guards
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cheap pre-flight: don't fire the RPC for empty/incomplete inputs that we
 * already know would 400. Cuts unnecessary network noise during page mount.
 */
function hasUsableInput(input: LandedCostPreviewInput): boolean {
  if (!input) return false;
  // trm_aduana ya NO es obligatoria: el RPC hace fallback a TRM histórica (modo estimado).
  if (!Array.isArray(input.items) || input.items.length === 0) return false;
  return true;
}
